'use client';

import { Fragment, useMemo, useState, type MouseEvent } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import { Spinner } from '~/components/ui/spinner';
import { serverError } from '~/utils/serverError';
import {
  CheckIcon,
  EllipsisHorizontalIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { type UndoDocumentIcon } from '~/components/Icons';
import { cn } from '~/lib/utils';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '~/components/ui/tooltip';

export type ActionButtonProps = {
  onEdit?: () => void;
  onDelete?: () => Promise<void> | void;
  onSave?: () => Promise<void>;
  onCancel?: () => void;
  isEditing?: boolean;
  disablePortal?: boolean;
  className?: string;
};

export type ActionOption = {
  icon: typeof PencilIcon | typeof UndoDocumentIcon;
  onClick: (e: MouseEvent) => void | Promise<void>;
  label: string;
  isWarning?: boolean;
  tooltip?: React.ReactNode;
  disabled?: boolean;
};

type SkeletonActionButtonProps = {
  options: ActionOption[];
  onOpen?: () => void | Promise<void>;
  disablePortal?: boolean;
  className?: string;
  /** Locks the kebab trigger (e.g. while a delayed undoable delete is pending). */
  disabled?: boolean;
};

export const SkeletonActionButton = (props: SkeletonActionButtonProps) => {
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        if (props.disabled) return;
        setOpen(next);
        if (next) props.onOpen?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Open actions"
          aria-haspopup="menu"
          disabled={props.disabled}
          className={cn(
            'text-muted-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors',
            'hover:bg-accent hover:text-foreground',
            'focus-visible:ring-ring/50 focus-visible:outline-hidden focus-visible:ring-[3px]',
            'data-[state=open]:bg-accent data-[state=open]:text-foreground',
            'disabled:pointer-events-none disabled:opacity-50',
            props.className,
          )}
        >
          <EllipsisHorizontalIcon className="size-4" strokeWidth={2} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="min-w-[10rem]"
        onClick={(e) => e.stopPropagation()}
      >
        <DropdownMenuGroup>
          {props.options.map((option, index) => {
            const prev = props.options[index - 1];
            const showSeparator = Boolean(option.isWarning && prev && !prev.isWarning);
            const item = (
              <DropdownMenuItem
                key={option.label}
                disabled={option.disabled || loadingIndex === index}
                className={cn(
                  option.isWarning &&
                    'text-destructive focus:bg-destructive/10 focus:text-destructive',
                )}
                onSelect={async (e) => {
                  e.preventDefault();
                  try {
                    if (option.disabled) return;
                    setLoadingIndex(index);
                    await option.onClick(e as unknown as MouseEvent);
                    setOpen(false);
                  } catch (err) {
                    serverError(err as Error);
                  } finally {
                    setLoadingIndex(null);
                  }
                }}
              >
                {loadingIndex === index ? (
                  <Spinner />
                ) : (
                  <option.icon strokeWidth={1.75} />
                )}
                {option.label}
              </DropdownMenuItem>
            );

            return (
              <Fragment key={option.label}>
                {showSeparator ? <DropdownMenuSeparator /> : null}
                {option.tooltip && option.disabled ? (
                  <TooltipProvider delayDuration={200}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span>{item}</span>
                      </TooltipTrigger>
                      <TooltipContent side="left">{option.tooltip}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : (
                  item
                )}
              </Fragment>
            );
          })}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default function ActionButton(props: ActionButtonProps) {
  const [isSaving, setIsSaving] = useState(false);

  const options = useMemo(() => {
    const opts: ActionOption[] = [];
    if (props.onEdit) {
      opts.push({
        icon: PencilIcon,
        label: 'Edit',
        onClick: props.onEdit,
      });
    }
    if (props.onDelete) {
      opts.push({
        icon: TrashIcon,
        label: 'Delete',
        onClick: props.onDelete,
        isWarning: true,
      });
    }
    return opts;
  }, [props.onEdit, props.onDelete]);

  if (props.isEditing) {
    return (
      <div className="flex items-center gap-0.5">
        {props.onSave && (
          <button
            type="button"
            aria-label="Save"
            disabled={isSaving}
            className={cn(
              'text-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors',
              'hover:bg-accent',
              'focus-visible:ring-ring/50 focus-visible:outline-hidden focus-visible:ring-[3px]',
              'disabled:pointer-events-none disabled:opacity-50',
            )}
            onClick={async (e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsSaving(true);
              try {
                await props.onSave?.();
              } catch (err) {
                serverError(err as Error);
              } finally {
                setIsSaving(false);
              }
            }}
          >
            {isSaving ? (
              <Spinner />
            ) : (
              <CheckIcon className="size-3.5" strokeWidth={2.25} />
            )}
          </button>
        )}
        {props.onCancel && (
          <button
            type="button"
            aria-label="Cancel"
            className={cn(
              'text-muted-foreground inline-flex size-7 items-center justify-center rounded-md transition-colors',
              'hover:bg-accent hover:text-foreground',
              'focus-visible:ring-ring/50 focus-visible:outline-hidden focus-visible:ring-[3px]',
            )}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              props.onCancel?.();
            }}
          >
            <XMarkIcon className="size-3.5" strokeWidth={2.25} />
          </button>
        )}
      </div>
    );
  }

  return (
    <SkeletonActionButton
      options={options}
      disablePortal={props.disablePortal}
      className={props.className}
    />
  );
}
